import React, {useMemo, useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import DisplayPrice from '../display-price'

// Components
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    useMultiStyleConfig,
    IconButton,
    Button,
    useDisclosure,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'

// Project Components
import {HeartIcon, HeartSolidIcon} from '@salesforce/retail-react-app/app/components/icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import Swatch from '@salesforce/retail-react-app/app/components/swatch-group/swatch'
import SwatchGroup from '@salesforce/retail-react-app/app/components/swatch-group'
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
import PromoCallout from './promo-callout'

// Hooks
import {FormattedMessage, useIntl} from 'react-intl'

// Other
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {productUrlBuilder, rebuildPathWithParams} from '../../utills/url'
import {findLowestPrice, getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {filterImageGroups, getDecoratedVariationAttributes} from '../../utills/product-utlis'
import CloudinaryPlpImage from '../../cloudinary/cloudinary-plp-images'
import QuickView from '../../pages/product-list/partials/quick-view'
import CloudinaryImageSwatches from '../../cloudinary/cloudinary-image-swatches'
import {pxToRem} from '../../utills/pxToRem'

const IconButtonWithRegistration = withRegistration(IconButton)

let bvRefreshTimeout = null
const scheduleBvRefresh = () => {
    if (typeof window === 'undefined') return
    if (bvRefreshTimeout) return
    bvRefreshTimeout = setTimeout(() => {
        bvRefreshTimeout = null
        window.$BV?.ui?.refresh?.()
    }, 150)
}

// Component Skeleton
const PricingAndPromotionsSkeleton = () => {
    return (
        <Stack spacing={2} data-testid="sf-product-tile-pricing-and-promotions-skeleton">
            <ChakraSkeleton width="80px" height="20px" />
            <ChakraSkeleton width={{base: '120px', md: '220px'}} height="12px" />
        </Stack>
    )
}

export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <PricingAndPromotionsSkeleton />
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = (props) => {
    const {
        dynamicImageProps,
        enableFavourite = false,
        imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE,
        isFavourite,
        onFavouriteToggle,
        product,
        selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID,
        isRefreshingData = false,
        showQuickView = true,
        previousSearchParams = {},
        onVisibilityChange = () => {},
        isGiftCardCategory = false,
        category,
        ...rest
    } = props
    const {imageGroups, productId, c_productURL, representedProduct, variants} = product
    const ref = useRef(null)
    const isVisible = useIntersectionObserver(ref, {threshold: 0.5})

    const prevVisibility = useRef(null)

    useEffect(() => {
        if (prevVisibility.current !== isVisible) {
            onVisibilityChange(product.productId, isVisible)
            prevVisibility.current = isVisible
        }
    }, [product.productId, isVisible, onVisibilityChange])

    const intl = useIntl()
    const {currency} = useCurrency()
    const isFavouriteLoading = useRef(false)
    const styles = useMultiStyleConfig('ProductTile')
    const isMobile = useBreakpointValue({base: true, md: false})
    const {isOpen, onOpen, onClose} = useDisclosure()

    const isMasterVariant = !!variants
    const initialVariationValue =
        isMasterVariant && !!representedProduct
            ? variants?.find((variant) => variant.productId == product.representedProduct.id)
                  ?.variationValues?.[selectableAttributeId]
            : undefined

    const [selectableAttributeValue, setSelectableAttributeValue] = useState(initialVariationValue)

    useEffect(() => {
        if (initialVariationValue && initialVariationValue !== selectableAttributeValue) {
            setSelectableAttributeValue(initialVariationValue)
        }
    }, [initialVariationValue])

    // Primary image for the tile, the image is determined from the product and selected variation attributes.
    const image = useMemo(() => {
        // NOTE: If the selectable variation attribute doesn't exist in the products variation attributes
        // array, lets not filter the image groups on it. This ensures we always return an image for non-variant
        // type products.
        const hasSelectableAttribute = product?.variationAttributes?.find(
            ({id}) => id === selectableAttributeId
        )

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}
        const filteredImageGroups = filterImageGroups(imageGroups || [], {
            viewType: imageViewType,
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        // Return the first image of the first group.
        return filteredImageGroups?.[0]?.images[0]
    }, [product, selectableAttributeId, selectableAttributeValue, imageViewType])

    // Primary URL user to wrap the ProduceTile.
    const cleanedUrl = c_productURL.replace('/s/Cavenders', '')
    const productUrl = useMemo(
        () =>
            rebuildPathWithParams(productUrlBuilder({productURL: cleanedUrl}), {
                [selectableAttributeId]: selectableAttributeValue
            }),
        [product, selectableAttributeId, selectableAttributeValue]
    )
    // NOTE: variationAttributes are only defined for master/variant type products.
    const variationAttributes = useMemo(() => {
        try {
            const attrs = getDecoratedVariationAttributes(product)
            return Array.isArray(attrs) ? attrs : []
        } catch (err) {
            return []
        }
    }, [product])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name ?? product.productName

    const productWithFilteredVariants = useMemo(() => {
        const rawVariants = Array.isArray(product?.variants) ? product.variants : []
        const variants = rawVariants
            .map((variant) => ({
                price: variant.price,
                productId: variant.productId,
                productPromotions: variant.productPromotions || [],
                tieredPrices: variant.tieredPrices,
                variationValues: variant.variationValues
            }))
            .filter(
                ({variationValues}) =>
                    variationValues?.[selectableAttributeId] === selectableAttributeValue
            )

        return {
            ...product,
            variants
        }
    }, [product, selectableAttributeId, selectableAttributeValue])

    // Pricing is dynamic! Ensure we are showing the right price for the selected variation attribute
    // value.
    const priceData = useMemo(() => {
        return getPriceData(productWithFilteredVariants)
    }, [productWithFilteredVariants])

    const hasPrice = useMemo(
        () => Number.isFinite(findLowestPrice(productWithFilteredVariants)?.data?.price),
        [productWithFilteredVariants]
    )

    const bazaarvoiceProductId = product?.productId

    useEffect(() => {
        if (!bazaarvoiceProductId) return
        scheduleBvRefresh()
    }, [bazaarvoiceProductId])

    const handleOpenQuickView = (e) => {
        e.preventDefault()
        onOpen()
    }
    return (
        <Box ref={ref} {...styles.container}>
            <Link data-testid="product-tile" to={productUrl} {...styles.link} {...rest}>
                <Box {...styles.imageWrapper}>
                    {product && product.c_cloudinary?.plpEnabled ? (
                        <AspectRatio {...styles.image}>
                            <CloudinaryPlpImage
                                cloudinaryImage={
                                    product?.c_cloudinary ? product.c_cloudinary : null
                                }
                                image={image}
                            />
                        </AspectRatio>
                    ) : (
                        <AspectRatio {...styles.image}>
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${
                                    image?.disBaseLink ||
                                    image?.link ||
                                    product?.image?.disBaseLink ||
                                    product?.image?.link
                                }[?sw={width}&q=60]`}
                                widths={dynamicImageProps?.widths}
                                imageProps={{
                                    // treat img as a decorative item, we don't need to pass `image.alt`
                                    // since it is the same as product name
                                    // which can cause confusion for individuals who uses screen readers
                                    alt: '',
                                    loading: 'lazy',
                                    width: 290,
                                    height: 290,
                                    decoding: 'async',
                                    style: {aspectRatio: '1/1'},
                                    ...dynamicImageProps?.imageProps
                                }}
                            />
                        </AspectRatio>
                    )}
                    {!isMobile && showQuickView && (
                        <Button
                            onClick={handleOpenQuickView}
                            variant="brown"
                            size="full"
                            className="qv-btn"
                            {...styles.quickViewBtn}
                        >
                            <FormattedMessage
                                defaultMessage="Quick View"
                                id="product_tile.btn.quickview.label"
                            />
                        </Button>
                    )}
                </Box>

                {/* Swatches */}
                {variationAttributes?.filter(({id}) => selectableAttributeId === id).length >= 1 &&
                    variationAttributes
                        ?.filter(({id}) => selectableAttributeId === id)
                        ?.map(({id, name, values}) =>
                            id === 'color' ? (
                                values.length > 0 &&
                                product?.c_cloudinary?.cldSwatches &&
                                values.map(({value}) => (
                                    <Box mt={2} mb={4} key={value}>
                                        <CloudinaryImageSwatches
                                            cloudinaryImageGallery={
                                                product?.c_cloudinary ? product.c_cloudinary : null
                                            }
                                            value={value}
                                            swatchStyles={{
                                                padding: [
                                                    pxToRem(1),
                                                    pxToRem(1),
                                                    pxToRem(2),
                                                    pxToRem(2)
                                                ],
                                                width: [
                                                    pxToRem(19),
                                                    pxToRem(19),
                                                    pxToRem(36),
                                                    pxToRem(36)
                                                ],
                                                height: [
                                                    pxToRem(19),
                                                    pxToRem(19),
                                                    pxToRem(36),
                                                    pxToRem(36)
                                                ]
                                            }}
                                        />
                                    </Box>
                                ))
                            ) : (
                                <SwatchGroup
                                    ariaLabel={name}
                                    key={id}
                                    value={selectableAttributeValue}
                                    handleChange={(value) => {
                                        setSelectableAttributeValue(value)
                                    }}
                                >
                                    {(values || [])?.map(({name, swatch, value}) => {
                                        const content = swatch ? (
                                            <Box
                                                height="100%"
                                                width="100%"
                                                minWidth="32px"
                                                backgroundRepeat="no-repeat"
                                                backgroundSize="cover"
                                                backgroundColor={name.toLowerCase()}
                                                backgroundImage={`url(${
                                                    swatch?.disBaseLink || swatch.link
                                                })`}
                                            />
                                        ) : (
                                            name
                                        )

                                        return (
                                            <Swatch
                                                key={value}
                                                value={value}
                                                name={name}
                                                variant={'circle'}
                                                isFocusable={true}
                                            >
                                                {content}
                                            </Swatch>
                                        )
                                    })}
                                </SwatchGroup>
                            )
                        )}

                {isRefreshingData ? (
                    <PricingAndPromotionsSkeleton />
                ) : (
                    <>
                        {/* Promotion call-out message */}
                        {productWithFilteredVariants?.c_promotionMessage && (
                            <PromoCallout
                                {...styles.promoCallout}
                                product={productWithFilteredVariants}
                                promotionMessage={productWithFilteredVariants.c_promotionMessage}
                            />
                        )}

                        {/* Title */}
                        <Text {...styles.title}>{localizedProductName}</Text>

                        {bazaarvoiceProductId && (
                            <Box
                                data-bv-show="inline_rating"
                                data-bv-product-id={bazaarvoiceProductId}
                                data-bv-seo="false"
                                width="100%"
                                minHeight="24px"
                            />
                        )}

                        <Box marginTop="auto">
                            {/* Price */}
                            {hasPrice && (
                                <DisplayPrice
                                    priceData={priceData}
                                    isPriceRange={isGiftCardCategory}
                                    currency={currency}
                                    currentPriceProps={
                                        priceData.isOnSale
                                            ? {...styles.price, ...styles.currentPrice}
                                            : {...styles.price}
                                    }
                                    listPriceProps={{...styles.price}}
                                />
                            )}
                        </Box>
                    </>
                )}
            </Link>
            {enableFavourite && (
                <Box
                    onClick={(e) => {
                        // stop click event from bubbling
                        // to avoid user from clicking the underlying
                        // product while the favourite icon is disabled
                        e.preventDefault()
                    }}
                >
                    <IconButtonWithRegistration
                        data-testid="wishlist-button"
                        aria-label={
                            isFavourite
                                ? intl.formatMessage(
                                      {
                                          id: 'product_tile.assistive_msg.remove_from_wishlist',
                                          defaultMessage: 'Remove {product} from wishlist'
                                      },
                                      {product: localizedProductName}
                                  )
                                : intl.formatMessage(
                                      {
                                          id: 'product_tile.assistive_msg.add_to_wishlist',
                                          defaultMessage: 'Add {product} to wishlist'
                                      },
                                      {product: localizedProductName}
                                  )
                        }
                        icon={isFavourite ? <HeartSolidIcon /> : <HeartIcon />}
                        {...styles.favIcon}
                        onClick={async () => {
                            if (!isFavouriteLoading.current) {
                                isFavouriteLoading.current = true
                                await onFavouriteToggle(!isFavourite)
                                isFavouriteLoading.current = false
                            }
                        }}
                    />
                </Box>
            )}
            {isOpen && (
                <QuickView
                    isOpen={isOpen}
                    onClose={onClose}
                    product={product}
                    previousSearchParams={previousSearchParams}
                    category={category}
                />
            )}
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

// Define the PropType for c_cloudinary
const cCloudinaryPropType = PropTypes.shape({
    isResponsive: PropTypes.bool,
    c_autoResponsiveDimensions: PropTypes.string,
    cldSwatches: PropTypes.arrayOf(
        PropTypes.shape({
            variationAttrValueID: PropTypes.string,
            cldUrl: PropTypes.string
        })
    ),
    cloudName: PropTypes.string,
    plpEnabled: PropTypes.bool,
    url: PropTypes.string
})

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variants: PropTypes.array,
        type: PropTypes.shape({
            set: PropTypes.bool,

            bundle: PropTypes.bool,
            item: PropTypes.bool
        }),
        c_cloudinary: cCloudinaryPropType,
        c_productURL: PropTypes.string
    }),
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * The `viewType` of the image component. This defaults to 'large'.
     */
    imageViewType: PropTypes.string,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    /**
     * Details of badge labels and the corresponding product custom properties that enable badges.
     */
    badgeDetails: PropTypes.array,
    /**
     * Determines whether to display a skeleton over personalizable data (e.g., pricing and promotions) during data refresh.
     */
    isRefreshingData: PropTypes.bool,
    showQuickView: PropTypes.bool,
    /**
     * Function to be called when the visibility of the product tile changes.
     * This is useful for analytics or lazy loading.
     */
    onVisibilityChange: PropTypes.func,
    /**
     * Previous search parameters to be passed to the QuickView component.
     * This is useful for maintaining the search context when opening the QuickView.
     */
    previousSearchParams: PropTypes.object,
    c_productURL: PropTypes.string,
    isGiftCardCategory: PropTypes.bool,
    category: PropTypes.string
}

export default ProductTile
